import {Component, ViewChild, ViewEncapsulation} from '@angular/core';
import {LangService, LoaderService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {FormGroup, Validators} from '@angular/forms';
import {CurrencySign, PassportType, PurposeOfVisit, RequestType, RequestWithDuplicata} from 'src/app/shared/models/request';
import {Document, Gender, UserPermission} from 'src/app/shared/models/user';
import {RequestService} from 'src/app/shared/services/request.service';
import {ApiService} from '../../../shared/services/api.service';
import {CartService} from '../../../shared/services/cart.service';
import {Direction, FormField, VisaFN, VisaFormConfig, VisaFormName} from '../../../shared/models/forms';
import {InactivityService} from '../../../shared/services/inactivity.service';
import {ScanDocumentService} from '../../../shared/services/scan-document.service';
import {format} from 'date-fns';
import {convertToEpoch} from 'src/app/shared/utils/date';
import {ActivatedRoute} from '@angular/router';
import {FormComponent} from '../../../components/form/form.component';
import {applySchemaValidations, isCanHaveOnArrival, isRequirePreapproval, setCurrentCountryCat} from '../../../shared/models/default-filter-strategy';
import {warnIfInvalidName} from 'src/app/shared/utils/form';
import {getEnumByRole} from '../../../shared/utils/getEnumByRole';
import {Subscription} from 'rxjs';
import {documentTypeIsVisaMultiple, documentTypeIsEntry} from 'src/app/shared/models/document-type-helpers';

@Component({
  selector: 'app-form-visa',
  templateUrl: './form-visa.component.html',
  styleUrl: './form-visa.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class FormVisaComponent {
  currentStep: { key: VisaFormName, value: VisaFormName };
  steps: { key: VisaFormName, value: VisaFormName }[];
  VisaFormName = VisaFormName;
  availableBatchIds: string[];
  schemaLoaded = false;
  VisaFN = VisaFN;
  keepScanned = false;
  RequestType = RequestType;
  schema: any;
  submitted: boolean;
  successOnArrival: boolean;
  document: Document;
  subscription: Subscription;
  showCamera = false;
  renderForm = true;

  config: VisaFormConfig;
  personId: number;
  paramDirection: Direction;

  @ViewChild(FormComponent) formComponent: FormComponent;

  constructor(public nav: NavigateService,
    public api: ApiService,
    public cart: CartService,
    public request: RequestService,
    public scanDoc: ScanDocumentService,
    private route: ActivatedRoute,
    private lang: LangService,
    private loader: LoaderService,
    private inactivity: InactivityService) {
    const webKiosk = sessionStorage.getItem('webKiosk') === 'true';
    this.route.queryParams.subscribe(params => {
      if(params && params.direction) this.paramDirection = params.direction;
    });
    this.subscription = this.cart.currentApplication$.subscribe(() => {
      if(!(this.cart.currentApplication?.isExtend || (this.cart.currentApplication?.isRevision && this.cart.currentApplication?.isExtend))) {
        if(!this.cart.currentRequest) {
          if(this.cart.currentApplication?.isRevision || (this.api.userRole.isWorker && this.api.hasPerm(UserPermission.ALLOW_CREATE) && this.api.hasPerm(UserPermission.ALLOW_CONSUME))) {
            this.nav.to('dashboard');
          } else if(this.api.userRole.isKiosk && !webKiosk) {
            this.nav.to('standby-kiosk');
          } else if(this.api.env.formHasTravelNameStep) {
            this.nav.to('travel-name');
          } else if(this.api.env.hasMultiPersonView) {
            this.nav.to('participants');
          }
        } else if(this.api.env.formHasPassportTypeStep && (!this.cart.currentRequest.nationality || !this.cart.currentRequest.passportType)) {
          this.nav.to('passport-type');
          return;
        } else if(!this.cart.currentRequest.visaType) {
          this.nav.to('visa-type');
          return;
        }
      }
      this.loader.loading(true);
      this.cart.currentRequest = {...this.cart.currentRequest, latestRouteVisited: 'form-visa'};
      this.cart.saveCurrentApplication(false);
      this.initConfig().then(() => {
        this.personId = this.cart.currentRequest.person.id;
        const currentSeniority = this.api.checkSeniority();
        const stepsValues = Object.keys(this.config) as VisaFormName[];
        this.steps = stepsValues.map((value: VisaFormName) => ({
          key: this.getKeyByValue(value, VisaFormName) as VisaFormName,
          value
        })).filter(step => {
          const stepConfig = this.config[step.value];
          if(!stepConfig) return false;
          if(stepConfig.disabledForRoles?.includes(this.api.userInfo.role)) return false;
          if(stepConfig.disabledForSeniorities?.includes(currentSeniority)) return false;
          return true;
        });

        if(this.cart.currentRequest.latestFormStepVisited) {
          this.currentStep = this.cart.currentRequest.latestFormStepVisited;
          this.loader.loading(false);
        } else {
          if(this.api.env.useGuichet && this.api.env.canDisablePlace && this.config[VisaFormName.MISCELLANEOUS_DETAILS]) {
            const field = this.config[VisaFormName.MISCELLANEOUS_DETAILS].fields.find(field => field.name === VisaFN.REGISTRATION_PORT_ID);
            if(field && field.cardChoice?.length) {
              const places = field.cardChoice.map(card => card.value);
              field.cardChoice = field.cardChoice.map(choice => ({...choice, disabled: true, description: this.lang.transform('timeslot.notAvailable')}));
              this.api.isOpenTimeslots(places).then(response => {
                field.cardChoice = field.cardChoice.map((choice) => {
                  const isOpen = response[choice.value];
                  return {...choice, disabled: !isOpen, description: this.lang.transform(isOpen === false ? 'timeslot.notAvailable' : '')};
                });
              }).catch(() => {
                field.cardChoice = field.cardChoice.map(choice => ({...choice, disabled: false, description: ''}));
              }).finally(() => {
                this.loader.loading(false);
              });
            }
          } else {
            this.loader.loading(false);
          }
          this.currentStep = this.steps[0];
          this.cart.currentRequest.latestFormStepVisited = this.currentStep;
        }
        this.schema = undefined;
        this.schemaLoaded = false;
        this.setSchema().then(() => {
          this.cart.saveCurrentApplication(false);
          if(this.scanDoc.scanDatas && this.scanDoc.scanned && (this.api.userRole.isWorker || this.api.userRole.isAdmin)) {
            this.preFillFormFromScanDoc();
          } else if(!this.cart.currentApplication?.isExtend) {
            this.preFillForm();
          }

          this.additionnalConditions().then(() => {
            this.schemaLoaded = true;
          });
        }).catch(console.error);

        if(this.cart.currentApplication.isRevision && this.cart.currentApplication.skipValidRevision) {
          //if param direction is previous and no errors on current step, do previous
          if(this.paramDirection === Direction.PREVIOUS && !this.config[this.currentStep.value].hasRevisionErrors) {
            this.previousStep();
          } else if(this.paramDirection === Direction.NEXT && !this.config[this.currentStep.value].hasRevisionErrors) {
            this.nextStep();
          }
        }
      });
    });
    if(!this.inactivity.isWatching && this.api.userRole.isKiosk) this.inactivity.startWatching();
  }

  ngOnDestroy() {
    if(!this.keepScanned) {
      this.scanDoc.scanned = false;
    }
    this.subscription.unsubscribe();
  }

  initConfig(): Promise<void> {
    return new Promise(resolve => {
      if(this.cart.currentRequest?.formConfig) {
        this.config = this.cart.currentRequest.formConfig;
      } else {
        this.config = JSON.parse(JSON.stringify(this.cart.getOriginalForm()));
      }

      Object.keys(this.config).forEach(step => {
        if(!this.cart.getOriginalForm()[step]) {
          this.config = JSON.parse(JSON.stringify(this.cart.getOriginalForm()));
        } else if(this.config[step] && this.config[step].version < this.cart.getOriginalForm()[step].version) {
          this.config[step] = JSON.parse(JSON.stringify(this.cart.getOriginalForm()[step]));
        }
      });

      resolve();
    });
  }

  additionnalConditions(): Promise<void> {
    return new Promise(resolve => {
      if(!this.api.env.useGuichet && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config) && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config).requireOneOf = [VisaFN.FIRST_NAME, VisaFN.LAST_NAME];
      }

      if(this.api.userRole.isAdmin || this.api.userRole.isWorker) {

        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.EXISTING_VALID_ENTRY_REFERENCE, this.config)) {
          if(this.cart.currentRequest.passportType === PassportType.ORDINARY && this.cart.currentRequest.nationality !== this.api.userInfo.server.country) {
            this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.EXISTING_VALID_ENTRY_REFERENCE, this.config).hidden = false;
          } else if(this.cart.currentRequest.passportType === PassportType.IDENTITY_CARD) {
            this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.EXISTING_VALID_ENTRY_REFERENCE, this.config).validators = [Validators.required.name];
            this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.EXISTING_VALID_ENTRY_REFERENCE, this.config).hidden = false;
          } else {
            this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.EXISTING_VALID_ENTRY_REFERENCE, this.config).validators = [];
            this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.EXISTING_VALID_ENTRY_REFERENCE, this.config).hidden = true;
          }
        }
      }

      if((this.cart.currentRequest.passportType === PassportType.RESIDENT_PERMIT || this.cart.currentRequest.passportType === PassportType.PERMANENT_RESIDENT_PERMIT) && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.RESIDENCE_PERMIT_NUMBER, this.config).hidden = false;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.RESIDENCE_PERMIT_NUMBER, this.config).validators = [Validators.required.name];

      } else if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).label = VisaFN.PASSPORT_NUMBER;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).disclaimer = '';
      }
      if((this.cart.currentRequest.passportType === PassportType.RESIDENT_PERMIT || this.cart.currentRequest.passportType === PassportType.PERMANENT_RESIDENT_PERMIT) && (this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE, this.config) || this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config))) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE, this.config).validators = [];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config).validators = [];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE, this.config).onlyCustomValidators = true;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config).onlyCustomValidators = true;
      }

      if(this.cart.currentRequest.passportType === PassportType.REFUGEE && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).hidden = false;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).validators = [Validators.required.name];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).onlyCustomValidators = true;

        if(this.scanDoc.scanDatas?.fields?.issuingState && this.request.getNationalities(false).some(nat => nat.value === this.scanDoc.scanDatas.fields.issuingState)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).value = this.scanDoc.scanDatas.fields.issuingState;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).disabled = true;
        }
      } else if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).hidden = true;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).validators = [];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, this.config).onlyCustomValidators = true;
      }

      if(this.api.env.useGuichet) {

        if(this.cart.currentRequest.passportType === PassportType.REFUGEE && this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config)) {
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).hidden = false;
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).validators = [Validators.required.name];
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).onlyCustomValidators = true;

          if(this.scanDoc.scanDatas?.fields?.issuingState && this.request.getNationalities(false).some(nat => nat.value === this.scanDoc.scanDatas.fields.issuingState)) {
            this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).value = this.scanDoc.scanDatas.fields.issuingState;
            this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).disabled = true;
          }
        } else if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config)) {
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).hidden = true;
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).validators = [];
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, this.config).onlyCustomValidators = true;
        }
      }

      if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DURATION_OF_STAY, this.config)) {
        const passportCountry = this.cart.currentRequest.nationality || this.cart.currentRequest.person?.nationality;
        if(
          this.api.env.nationalityNoDeclaration.includes(passportCountry) ||
          this.cart.currentRequest.passportType === PassportType.RESIDENT_PERMIT ||
          this.cart.currentRequest.passportType === PassportType.PERMANENT_RESIDENT_PERMIT || 
          (this.cart.currentRequest.visaType?.id && documentTypeIsEntry(this.cart.currentRequest.visaType.id))
        ) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DURATION_OF_STAY, this.config).hidden = true;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DURATION_OF_STAY, this.config).disabled = true;
        } else {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DURATION_OF_STAY, this.config).validators = [Validators.required.name];
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DURATION_OF_STAY, this.config).onlyCustomValidators = true;
        }
      }

      if(this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PREVIOUS_PASSPORT_NUMBER, this.config)) {
        if(this.cart?.currentRequest?.selectedRequestReason === 'FIRST_REQUEST') {
          this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PREVIOUS_PASSPORT_NUMBER, this.config).hidden = true;
          this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PREVIOUS_PASSPORT_NUMBER, this.config).validators = [];
          this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PREVIOUS_PASSPORT_NUMBER, this.config).onlyCustomValidators = true;
        }
      }

      if(this.api.userRole.isCustomer && this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_TYPE, this.config)) {
        this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_TYPE, this.config).value = PassportType.ORDINARY;
        this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_TYPE, this.config).disabled = true;
        this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_TYPE, this.config).hidden = true;
      }

      resolve();
    });
  }

  preFillForm() {
    if(this.api.userRole.isAdmin || this.api.userRole.isWorker) {
      if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, this.config).validators = [Validators.required.name];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, this.config).onlyCustomValidators = true;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM, this.config).validators = [];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM, this.config).onlyCustomValidators = true;
      }
      if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ASK, this.config).validators = [Validators.required.name];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ASK, this.config).onlyCustomValidators = true;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS, this.config).validators = [];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS, this.config).onlyCustomValidators = true;
      }
      if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME, this.config)) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, this.config).validators = [Validators.required.name];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, this.config).onlyCustomValidators = true;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME, this.config).validators = [];
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME, this.config).onlyCustomValidators = true;
      }
    }

    if(this.api.userRole.isCustomer && !this.api.env.nationalityNoDeclaration.includes(this.cart.currentRequest.nationality) && this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_LAST_NAME, this.config)) {
      this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_FIRST_NAME, this.config).validators = [Validators.required.name];
      this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_EMAIL, this.config).validators = [Validators.required.name];
      this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_PHONE, this.config).validators = [Validators.required.name];
    }
    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config) &&
      !this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config)?.value &&
      this.cart.currentRequest?.passportType !== PassportType.RESIDENT_PERMIT &&
      this.cart.currentRequest?.passportType !== PassportType.PERMANENT_RESIDENT_PERMIT) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).value = this.cart.currentRequest.person.passportNumber;
    } else if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config) &&
      !this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config)?.value &&
      this.cart.currentRequest?.passportType !== PassportType.RESIDENT_PERMIT &&
      this.cart.currentRequest?.passportType !== PassportType.PERMANENT_RESIDENT_PERMIT) {
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config).value = this.cart.currentRequest.person.passportNumber;
    }
    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config) && !this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config)?.value && !this.cart.currentApplication?.isRevision) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config).value = this.cart.currentRequest.person.firstName;
      if(this.cart?.currentRequest?.person?.firstName) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config).disabled = true;
      }
    }
    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config) && !this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config)?.value && !this.cart.currentApplication?.isRevision) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config).value = this.cart.currentRequest.person.lastName;
      if(this.cart?.currentRequest?.person?.lastName) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config).disabled = true;
      }
    }
    if(this.cart.currentRequest.passportType && this.cart.currentRequest.passportType !== PassportType.ORDINARY &&
      this.cart.currentRequest.passportType !== PassportType.REFUGEE &&
      this.cart.currentRequest.passportType !== PassportType.IDENTITY_CARD &&
      this.cart.currentRequest.passportType !== PassportType.RESIDENT_PERMIT && !this.api.env.useGuichet) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).value = this.cart.currentRequest.passportType === PassportType.PERMANENT_RESIDENT_PERMIT ? PurposeOfVisit.LIVE_HERE : PurposeOfVisit.BUSINESS_VISA;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).hidden = true;
      if(this.cart.currentRequest.passportType !== PassportType.PERMANENT_RESIDENT_PERMIT) this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.NON_ORDINARY_PASS_MISSION, this.config).hidden = false;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE, this.config).value = '0';
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config).value = 'USD';
    }
    if(this.scanDoc.consumeMode === 'transit') {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).value = PurposeOfVisit.TRANSIT_VISA;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).disabled = true;
    }

    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config) && this.cart?.currentRequest?.selectedRequestReason) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).value = this.cart.currentRequest.selectedRequestReason;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).hidden = true;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).disabled = true;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).validators = [];
    }

    // Auto-fill REGISTRATION_PORT_ID for workers with their current place (forced and disabled)
    if(this.api.userRole.isWorker && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, this.config) && this.api.userPlaceId) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, this.config).value = this.api.userPlaceId;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, this.config).disabled =
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, this.config).value === this.api.userPlaceId;
    }

    if(this.request.getField(VisaFormName.DOCUMENT, VisaFN.PREVIOUS_VISA, this.config)) {
      if(documentTypeIsVisaMultiple(this.cart.currentRequest?.visaType?.id)) {
        this.request.getField(VisaFormName.DOCUMENT, VisaFN.PREVIOUS_VISA, this.config).validators = [Validators.required.name];
        this.request.getField(VisaFormName.DOCUMENT, VisaFN.PREVIOUS_VISA, this.config).hidden = false;
      }
    }
  }

  preFillFormFromScanDoc() {
    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM, this.config)) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, this.config).validators = [Validators.required.name];
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM_ASK, this.config).onlyCustomValidators = true;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM, this.config).validators = [];
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ZIM, this.config).onlyCustomValidators = true;
    }
    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS, this.config)) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ASK, this.config).validators = [Validators.required.name];
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_ASK, this.config).onlyCustomValidators = true;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS, this.config).validators = [];
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS, this.config).onlyCustomValidators = true;
    }
    if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME, this.config)) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, this.config).validators = [Validators.required.name];
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME_ASK, this.config).onlyCustomValidators = true;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME, this.config).validators = [];
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PREVIOUS_CONVICTIONS_HOME, this.config).onlyCustomValidators = true;
    }
    //last name
    if(this.scanDoc.scanDatas?.fields?.lastName && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config)) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config).value = this.scanDoc.scanDatas.fields.lastName;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config).disabled = this.scanDoc.scanDatas.valid && /^[A-Z' -]*$/.test(this.scanDoc.scanDatas.fields.lastName);
    }
    //first name
    if(this.scanDoc.scanDatas?.fields?.firstName && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config)) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config).value = this.scanDoc.scanDatas.fields.firstName;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config).disabled = this.scanDoc.scanDatas.valid && /^[A-Z' -]*$/.test(this.scanDoc.scanDatas.fields.firstName);
    }
    //birth date
    if(this.scanDoc.scanDatas?.fields?.birthDate && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_DAY, this.config)) {
      const parsedDate = convertToEpoch(this.scanDoc.scanDatas.fields.birthDate, true, true);
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_DAY, this.config).value = format(parsedDate, 'yyyy-MM-dd');
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_DAY, this.config).disabled = this.scanDoc.scanDatas.valid;
    }
    //gender
    if(this.scanDoc.scanDatas?.fields?.sex && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.GENDER, this.config)) {
      const male = (this.scanDoc.scanDatas.fields.sex.toLowerCase() === 'male' || this.scanDoc.scanDatas.fields.sex.toLowerCase() === 'm') ? Gender.MALE : undefined;
      const female = (this.scanDoc.scanDatas.fields.sex.toLowerCase() === 'female' || this.scanDoc.scanDatas.fields.sex.toLowerCase() === 'f') ? Gender.FEMALE : undefined;
      if(male || female) {
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.GENDER, this.config).value = male || female;
        this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.GENDER, this.config).disabled = this.scanDoc.scanDatas.valid;
      }
    }
    //passport number && residence permit number
    if(this.scanDoc.scanDatas?.fields?.documentNumber) {
      if(this.scanDoc.scanDatas?.format === 'TD1') {
        //scanned a residence permit
        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.RESIDENCE_PERMIT_NUMBER, this.config)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.RESIDENCE_PERMIT_NUMBER, this.config).value = this.scanDoc.scanDatas.fields.documentNumber;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.RESIDENCE_PERMIT_NUMBER, this.config).disabled = this.scanDoc.scanDatas.valid;
        }

        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).value = this.scanDoc.scanDatas.fields.documentNumber;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).disabled = this.scanDoc.scanDatas.valid;
        }
        if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config)) {
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config).value = this.scanDoc.scanDatas.fields.documentNumber;
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config).disabled = this.scanDoc.scanDatas.valid;
        }
      } else {
        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).value = this.scanDoc.scanDatas.fields.documentNumber;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, this.config).disabled = this.scanDoc.scanDatas.valid;
        }
        if(this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config)) {
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config).value = this.scanDoc.scanDatas.fields.documentNumber;
          this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_NUMBER, this.config).disabled = this.scanDoc.scanDatas.valid;
        }
      }
    }

    //passport expiry date
    if(this.scanDoc.scanDatas?.fields?.expirationDate && this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_EXPIRY, this.config)) {
      const parsedDate = convertToEpoch(this.scanDoc.scanDatas.fields.expirationDate, true);
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_EXPIRY, this.config).value = parsedDate;
      this.request.getField(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_EXPIRY, this.config).disabled = this.scanDoc.scanDatas.valid;
    }

    if(this.scanDoc.scanDatas?.fields?.expirationDate && this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DATE_OF_EXPIRY, this.config)) {
      const parsedDate = convertToEpoch(this.scanDoc.scanDatas.fields.expirationDate, true);
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DATE_OF_EXPIRY, this.config).value = format(parsedDate, 'yyyy-MM-dd');
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.DATE_OF_EXPIRY, this.config).disabled = this.scanDoc.scanDatas.valid;
    }

    if(this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_LAST_NAME, this.config)) {
      this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_FIRST_NAME, this.config).validators = [];
      this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_EMAIL, this.config).validators = [];
      this.request.getField(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMERGENCY_PHONE, this.config).validators = [];
    }

    if(this.cart.currentRequest.passportType !== PassportType.ORDINARY &&
      this.cart.currentRequest.passportType !== PassportType.REFUGEE &&
      this.cart.currentRequest.passportType !== PassportType.IDENTITY_CARD &&
      this.cart.currentRequest.passportType !== PassportType.RESIDENT_PERMIT) {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).value = this.cart.currentRequest.passportType === PassportType.PERMANENT_RESIDENT_PERMIT ? PurposeOfVisit.LIVE_HERE : PurposeOfVisit.BUSINESS_VISA;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).hidden = true;
      if(this.cart.currentRequest.passportType !== PassportType.PERMANENT_RESIDENT_PERMIT) this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.NON_ORDINARY_PASS_MISSION, this.config).hidden = false;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE, this.config).value = '0';
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config).value = 'USD';
    }
    if(this.scanDoc.consumeMode === 'transit') {
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).value = PurposeOfVisit.TRANSIT_VISA;
      this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).disabled = true;
    }
  }

  getPrice() {
    if(!this.cart.currentRequest) return;
    return this.document.currency + ' ' + Math.floor((this.document.amountCentsDyn ?
      eval(this.document.amountCentsDyn)({Nationality: this.cart.currentRequest.nationality, Urgent: this.cart.currentRequest.urgent}, this.api.userPlace) : this.document.amountCents) / 100);
  }

  submitStep(event: {form?: FormGroup, byPass?: boolean}) {
    if(event?.form?.pending) {
      const sub = event.form.statusChanges.subscribe((status) => {
        if(status !== 'PENDING') {
          sub.unsubscribe();
          this.submitStep(event);
        }
      });
      return;
    }

    if(!event?.form?.valid && !event?.form?.disabled && !event?.byPass) {
      return;
    }
    if(this.currentStep?.value === VisaFormName.PERSONAL_DETAILS) {

      const firstName = event?.form?.get(VisaFN.FIRST_NAME)?.value?.trim() || this.request?.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, this.config)?.value?.trim();
      const lastName = event?.form?.get(VisaFN.LAST_NAME)?.value?.trim() || this.request?.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, this.config)?.value?.trim();

      if(firstName === undefined && lastName === undefined) {
        this.nextStep();
        return;
      }


      if(!this.api.env.useGuichet) {
        warnIfInvalidName(firstName, lastName, this.loader).then(canContinue => {
          if(!canContinue) {
            return;
          }
          this.nextStep();
        });
      } else {
        this.nextStep();
      }
    } else {
      this.nextStep();
    }
  }

  setStep(i: number) {
    //if step is not visited or is current step, do nothing
    if(!this.config[this.steps[i].value].visited || this.currentStep.key === this.steps[i].key) {
      return;
    }
    if(this.formComponent && this.formComponent.form) {
      const formGroup = this.formComponent.form;
      const fields = this.config[this.currentStep.value]?.fields;
      if(fields && formGroup) {
        fields.forEach(field => {
          if(formGroup.get(field.name) !== null) {
            field.value = formGroup.get(field.name).value;
          }
        });
      }
    }
    this.updateFromSchema().then(() => {
      this.currentStep = this.steps[i];
      this.cart.currentRequest.latestFormStepVisited = this.currentStep;
    });
  }

  nextStep() {
    setTimeout(() => {
      const index = this.steps.findIndex((step: { key: VisaFormName, value: VisaFormName }) => step.key === this.currentStep.key);
      if(index < this.steps.length - 1) {
        if(this.cart.currentApplication.isRevision && this.cart.currentApplication.skipValidRevision) {
          let nextIndex = index + 1;
          while(nextIndex < this.steps.length - 1 && !this.config[this.steps[nextIndex].value].hasRevisionErrors) {
            nextIndex++;
          }
          if(nextIndex === this.steps.length - 1 && !this.config[this.steps[nextIndex].value].hasRevisionErrors && (this.api.userRole.isCustomer ? this.cart.personRequestIsValid(this.cart.currentRequest.person) : true)) {
            //if no errors found in next steps, nav to summary
            this.nav.to('summary');
          } else {
            this.updateFromSchema().then(() => {
              this.currentStep = this.steps[nextIndex] || this.steps[index + 1];
              this.cart.currentRequest.latestFormStepVisited = this.currentStep;
            });
          }
        } else {
          this.updateFromSchema().then(() => {
            const nextIndex = this.findStepWithVisibleFields(index, 'next');
            this.currentStep = this.steps[nextIndex] || this.steps[index + 1];
            this.cart.currentRequest.latestFormStepVisited = this.currentStep;
          });
        }
      } else if(this.cart.allPersonRequestsAreValid()) {
        if(this.api.userRole.isKiosk || this.api.userRole.isCustomer) {
          this.nav.to('summary');
        } else {
          this.loader.loading(true, {type: 'info', message: 'entry.confirmation', btnLabel: 'global.confirm', custom: {closeBtnLabel: 'no'}}).then((done: boolean) => {
            if(done) {
              if(this.scanDoc?.scanned) {
                this.keepScanned = true;
                this.scanDoc.visaDatas = [];
                sessionStorage.removeItem('visaDatas');
              }
              this.cart.currentApplication.visaFeesAndTerms = true;
              this.cart.saveCurrentApplication(false);
              return this.request.pay(false).catch((err) => {
                this.loader.loading(true, {type: 'error', message: err});
              });
            }
          });
        }
      } else if(this.api.userRole.isCustomer) {
        this.cart.openParticipants();
      }

      if(this.scanDoc.scanDatas && this.scanDoc.scanned && (this.api.userRole.isWorker || this.api.userRole.isAdmin)) {
        this.preFillFormFromScanDoc();
      } else if(!this.cart.currentApplication?.isExtend) {
        this.preFillForm();
      }
    });
  }

  previousStep() {
    const index = this.steps.findIndex((step: {
      key: VisaFormName,
      value: VisaFormName
    }) => step.key === this.currentStep.key);
    if(index > 0) {
      if(this.cart.currentApplication.isRevision && this.cart.currentApplication.skipValidRevision) {
        let prevIndex = index - 1;
        while(prevIndex > 0 && this.config[this.steps[prevIndex].value].visited && !this.config[this.steps[prevIndex].value].hasRevisionErrors) {
          prevIndex--;
        }
        //if no errors found in previous steps, nav to summary
        if(prevIndex === 0 && !this.config[this.steps[prevIndex].value].hasRevisionErrors) {
          if(this.api.env.formHasUrgentStep && this.request.sometimesPaidOnline(this.cart.currentRequest.visaType.id) && !this.api.userRole.isWorker && !this.api.userRole.isAdmin) {
            this.nav.to('urgent', undefined, {queryParams: {direction: Direction.PREVIOUS}});
          } else {
            this.nav.to('visa-type', undefined, {queryParams: {direction: Direction.PREVIOUS}});
          }
        } else {
          this.updateFromSchema().then(() => {
            this.currentStep = this.steps[prevIndex] || this.steps[index - 1];
            this.cart.currentRequest.latestFormStepVisited = this.currentStep;
          });
        }
      } else {
        // renderForm is used to fix slow form fields rendering when clicking the Previous button
        this.renderForm = false;
        this.updateFromSchema().then(() => {
          const prevIndex = this.findStepWithVisibleFields(index, 'previous');
          this.currentStep = this.steps[prevIndex] || this.steps[index - 1];
          this.cart.currentRequest.latestFormStepVisited = this.currentStep;
        });
      }
    } /*else if(this.cart.cartDatas?.fromRoute.includes('tracking-control')) {
      this.nav.goBack(); //TODO check revision
    }*/ else {
      if(this.cart.currentApplication?.isExtend) {
        this.nav.to('visa-extend', undefined, {queryParams: {direction: Direction.PREVIOUS}});
      } else if(this.api.env.formHasUrgentStep && this.request.sometimesPaidOnline(this.cart.currentRequest.visaType.id) && !this.api.userRole.isWorker && !this.api.userRole.isAdmin) {
        this.nav.to('urgent', undefined, {queryParams: {direction: Direction.PREVIOUS}});
      } else if(this.api.env.formReasonRequestStep) {
        this.nav.to('reason-request', undefined, {queryParams: {direction: Direction.PREVIOUS}});
      } else {
        if(this.scanDoc?.scanned) {
          this.keepScanned = true;
        }
        this.nav.to('visa-type', undefined, {queryParams: {direction: Direction.PREVIOUS}});
      }
    }

    if(this.scanDoc.scanDatas && this.scanDoc.scanned && (this.api.userRole.isWorker || this.api.userRole.isAdmin)) {
      this.preFillFormFromScanDoc();
    } else if(!this.cart.currentApplication?.isExtend) {
      this.preFillForm();
    }
  }

  ngAfterViewChecked() {
    this.renderForm = true;
  }

  setSchema() {
    return new Promise((resolve, reject) => {
      if(!this.schema) {
        this.document = this.api.userInfo.server.paymentRequests.find(p => p.id === this.cart.currentRequest.visaType.id);
        if(this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.DEPARTURE_DATE, this.config)) {
          const deltaMillis = eval(this.document.usageUntilFromNowMillis)({
            Nationality: this.cart.currentRequest.nationality,
            PassportType: this.cart.currentRequest.passportType
          });
          if(deltaMillis) this.request.getField(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.DEPARTURE_DATE, this.config).dateValidators.maxDateMilliFrom = {
            control: VisaFN.ARRIVAL_DATE,
            milliseconds: deltaMillis
          };
        }
        return this.api.schema(this.document.schema).then((res: any) => {
          this.schema = res;
          this.updateFromSchema().then(() => {
            resolve(true);
          }).catch(reject);
        }).catch(reject);
      } else {
        return this.updateFromSchema().then(() => {
          resolve(true);
        }).catch(reject);
      }
    });
  }

  updateFromSchema() {
    return new Promise(resolve => {
      if(this.schema?.properties) {

        // Apply schema validations for COMPASS type using the centralized method
        const document: Document = this.api.userInfo.server.paymentRequests.find(p => p.id === this.cart.currentRequest.visaType.id);
        applySchemaValidations(this, document.id);

        // Update enums from schema using helper method
        this.updateFieldEnumFromSchema(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.REGISTRATION_PORT_ID, 'RegistrationPortId', {transformKey: ''});
        this.updateFieldEnumFromSchema(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.REGISTRATION_TYPE, 'RegistrationType', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_TYPE, 'PassportType', {transformKey: 'DYN.', nameTransform: 'lowercase'});
        this.updateFieldEnumFromSchema(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_URGENT_DELIVERY, 'PassportUrgentDelivery', {transformKey: '', nameTransform: 'lowercase'});

        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.PARENT_GUARDIAN_CONSENT, 'ParentGuardianConsent', {nameTransform: 'lowercase'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.HAIR_COLOR, 'HairColor', {transformKey: '', valueTransform: 'uppercase', nameTransform: 'uppercase'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_COUNTRY, 'BirthCountry', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.EYE_COLOR, 'EyeColor', {transformKey: '', valueTransform: 'uppercase', nameTransform: 'uppercase'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, 'RegistrationPortId', {customNameValue: true});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.GENDER, 'Gender', {valueTransform: 'lowercase', nameTransform: 'lowercase'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH_COUNTRY, 'PlaceOfBirthCountry', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.ISLAND_ORIGIN, 'IslandOrigin', {customNameValue: true});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_TYPE, 'PassportType', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_TYPE, 'PassportType', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.PROVISIONAL_ENTRY_PORT, 'ProvisionnalEntryPort', {transformKey: ''});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.NATIONALITY, 'Nationality', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.RESIDENT_PERMIT_TYPE, 'ResidentPermitType', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.TRAVEL_INFORMATION, VisaFN.PASSPORT_COUNTRY, 'PassportCountry', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_COUNTRY, 'PassportCountry', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.MISCELLANEOUS_DETAILS, VisaFN.PASSPORT_COUNTRY, 'PassportCountry', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.ATTESTANT_RELATION, 'AttestantRelation', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.COUNTRY_OF_BIRTH, 'CountryOfBirth', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.MOTHER_COUNTRY_OF_BIRTH, 'CountryOfBirth', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.FATHER_COUNTRY_OF_BIRTH, 'CountryOfBirth', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.MARITAL_STATUS, 'MaritalStatus', {transformKey: 'DYN.', nameTransform: 'lowercase'});
        this.updateFieldEnumFromSchema(VisaFormName.PERSONAL_DETAILS, VisaFN.MODE_OF_TRAVEL, 'ModeOfTravel', {transformKey: 'DYN.'});
        this.updateFieldEnumFromSchema(VisaFormName.TRAVEL_INFORMATION, VisaFN.MODE_OF_TRAVEL, 'ModeOfTravel', {transformKey: 'DYN.'});

        // Special case for PURPOSE_OF_VISIT (nested property path)
        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, this.config).enum = this.schema.properties.Documents.properties.PurposeOfVisit?.enum.map((item: string) => ({
            value: item,
            name: this.lang.transform('DYN.' + item)
          })).sort((a: {name: string}, b: {name: string}) => a.name.localeCompare(b.name));
        }

        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH_COUNTRY, this.config)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH_COUNTRY, this.config).enum = this.request.getNationalities(false);
        }

        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_COUNTRY, this.config)) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_COUNTRY, this.config).enum = this.request.getNationalities(false);
        }

        if(this.request.getField(VisaFormName.APPLICANT_INFORMATION, VisaFN.BIRTH_COUNTRY, this.config)) {
          this.request.getField(VisaFormName.APPLICANT_INFORMATION, VisaFN.BIRTH_COUNTRY, this.config).enum = this.request.getNationalities(false);
        }

        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config) && this.cart?.currentRequest?.selectedRequestReason) {
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).value = this.cart.currentRequest.selectedRequestReason;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).hidden = true;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).disabled = true;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REQUEST_REASON, this.config).validators = [];
        }
        // Special case for FUNDS_AVAILABLE_CURRENCY (complex logic)
        if(this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config)) {
          const supportedCurrencies = this.api.env.supportedCurrencies;
          this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, this.config).enum = Object.keys(this.api.userInfo.rates.rates).map((item: string) => ({
            value: item,
            name: item
          })).sort((a: { name: string }, b: { name: string }) => {
            const aIndex = supportedCurrencies.indexOf(a.name);
            const bIndex = supportedCurrencies.indexOf(b.name);
            if(aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if(aIndex !== -1) return -1;
            if(bIndex !== -1) return 1;
            return a.name.localeCompare(b.name);
          }).filter((item: {name: string}) => item.name.length < 4);
        }

        // Special case for BIOMETRIC_DETAILS::SIGNATURE
        if(this.request.getField(VisaFormName.BIOMETRIC_DETAILS, VisaFN.SIGNATURE, this.config)) {
          this.request.getField(VisaFormName.BIOMETRIC_DETAILS, VisaFN.SIGNATURE, this.config).type = 'signature';
        }

        // Special case for BIOMETRIC_DETAILS::FINGERPRINTS
        if(this.request.getField(VisaFormName.BIOMETRIC_DETAILS, VisaFN.FINGERPRINTS, this.config)) {
          this.request.getField(VisaFormName.BIOMETRIC_DETAILS, VisaFN.FINGERPRINTS, this.config).type = 'fingerprints';
        }

        const fRegistrationPortId = this.request.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.REGISTRATION_PORT_ID, this.config);
        if(fRegistrationPortId?.enum?.length === 1) { 
          fRegistrationPortId.value = fRegistrationPortId.enum[0].value; 
          fRegistrationPortId.hidden = false; 
          fRegistrationPortId.disabled = true; 
        }

        // Apply conditional rules inside Documents
        if(this.schema.properties.Documents?.allOf) {
          this.processConditionalRules(this.schema.properties.Documents.allOf, 'Documents');
        } else if(this.schema.properties.Documents?.required) {
          this.schema.properties.Documents.required.forEach((field: string) => {
            if(this.request.getField(VisaFormName.DOCUMENT, field, this.config)?.name) {
              this.request.getField(VisaFormName.DOCUMENT, field, this.config).validators = [Validators.required.name];
            }
          });
        }

        // Apply top-level conditional rules (root allOf)
        if(Array.isArray(this.schema.allOf) && this.schema.allOf.length) {
          this.processConditionalRules(this.schema.allOf, 'root');
        }
        Object.keys(this.schema.properties).forEach((key) => {
          const fieldSchema = this.schema.properties[key];
          if(fieldSchema.maxLength !== undefined) {
            Object.values(VisaFormName).forEach((formName) => {
              const formField = this.request.getField(formName, key, this.config);
              if(formField) {
                formField.validators = formField.validators || [];
                formField.maxLength = fieldSchema.maxLength;
                formField.validators.push(Validators.maxLength(fieldSchema.maxLength));
              }
            });
          }
        });
        resolve(true);
      }
    });
  }

  allStepsValid(): boolean {
    //check if all steps except payment were visited and have no errors
    return this.steps.every((step: {
      key: VisaFormName,
      value: VisaFormName
    }) => this.config[step.value].visited && !this.config[step.value].errors?.length);
  }

  getKeyByValue(value: string, enumCheck: any): string | undefined {
    for(const key in enumCheck) {
      if(enumCheck[key] === value) {
        return key;
      }
    }
    return undefined;
  }

  isLastStep() {
    return this.currentStep && this.steps.length && this.currentStep.key === this.steps[this.steps.length - 1].key;
  }

  submit() {
    if(this.api.userRole.isWorker || this.api.userRole.isAdmin) {
      if(!this.api.userRole.isKiosk && this.cart.currentApplication && !this.cart.currentApplication.isRevision && !this.cart.currentApplication.isExtend && !this.cart.currentApplication.isResume && documentTypeIsEntry(this.cart.currentRequest.visaType.id)) {
        if(this.cart.currentRequest &&  !isCanHaveOnArrival({countryCat: setCurrentCountryCat({indexed: {Nationality: this.cart.currentRequest.nationality}})})) {
          const basePriceMetadata = {...this.cart.currentRequest.metadata, Urgent: 'No', SupervisorUsed: isRequirePreapproval(this.cart.currentRequest as RequestWithDuplicata, this.api.env)};
          const basePrice = Math.floor((this.document.amountCentsDyn ? eval(this.document.amountCentsDyn)(basePriceMetadata, this.api.userPlace) : this.document.amountCents) / 100);
          if(basePrice > 0) {
            this.cart.currentRequest.urgent = 'Yes';
          }
        }
      }
      this.formComponent.submitForm();
    }
  }

  hasVisibleFields(stepValue?: VisaFormName): boolean {
    const formName = stepValue || this.currentStep?.value;
    if(!this.config?.[formName]?.fields) return false;
    return this.config[formName].fields.some((field: FormField) => !field.hidden && this.formComponent.checkIfFormControl(field));
  }

  findStepWithVisibleFields(startIndex: number, direction: 'next' | 'previous'): number {
    let targetIndex = direction === 'next' ? startIndex + 1 : startIndex - 1;
    while(direction === 'next' ? targetIndex < this.steps.length : targetIndex >= 0) {
      const step = this.steps[targetIndex];
      if(this.hasVisibleFields(step.value)) {
        return targetIndex;
      }
      this.config[step.value].visited = true;
      this.config[step.value].submitted = true;
      this.config[step.value].errors = [];
      this.config[step.value].hasRevisionErrors = false;
      targetIndex = direction === 'next' ? targetIndex + 1 : targetIndex - 1;
    }
    return direction === 'next' ? startIndex + 1 : startIndex - 1;
  }

  private updateFieldEnumFromSchema(formName: VisaFormName,
    fieldKey: VisaFN,
    schemaProperty: string,
    options: {
      transformKey?: string; // e.g., 'DYN.' or empty for no transform
      valueTransform?: 'none' | 'lowercase' | 'uppercase'; // how to transform the value
      nameTransform?: 'none' | 'lowercase' | 'uppercase'; // how to transform the display name
      useCustomEnum?: () => any[]; // custom enum function (e.g., getNationalities)
      customNameValue?: boolean; // if true, name = value (no transform)
    } = {}): void {

    const field = this.request.getField(formName, fieldKey, this.config);
    if(!field) return;

    // Supporte les propriétés imbriquées dans Documents
    let propertySchema;
    if(schemaProperty && this.schema.properties.Documents?.properties?.[schemaProperty]) {
      propertySchema = this.schema.properties.Documents.properties[schemaProperty];
    } else if(schemaProperty && this.schema.properties[schemaProperty]) {
      propertySchema = this.schema.properties[schemaProperty];
    } else {
      propertySchema = undefined;
    }

    const schemaEnum = getEnumByRole(propertySchema, this.api.userInfo.role);

    if(Array.isArray(schemaEnum) && schemaEnum.length > 0) {
      const transformKey = options.transformKey || '';
      const valueTransform = options.valueTransform || 'none';
      const nameTransform = options.nameTransform || 'none';

      const mapped = schemaEnum.map((item: string) => {
        let value = item;
        let name = item;

        // Apply value transformation
        switch(valueTransform) {
        case 'lowercase':
          value = item.toLowerCase();
          break;
        case 'uppercase':
          value = item.toUpperCase();
          break;
        }

        // Apply name transformation
        if(options.customNameValue) {
          name = value;
        } else {
          switch(nameTransform) {
          case 'lowercase':
            name = this.lang.transform(transformKey + item.toLowerCase());
            break;
          case 'uppercase':
            name = this.lang.transform(transformKey + item.toUpperCase());
            break;
          default:
            name = this.lang.transform(transformKey + item);
          }
        }

        return {value, name};
      });
      // dedupe by value after transform
      const uniqByValue = mapped.filter((opt: any, idx: number, arr: any[]) => arr.findIndex((o: any) => o.value === opt.value) === idx);
      field.enum = uniqByValue.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));

      // Auto-select and hide fields with single enum option that are required
      this.handleSingleEnumAutoSelection(field, fieldKey);
      return;
    }

    // If schema declares segmented enums (enumA/enumB/enumC), default to nationalities
    if(propertySchema && (Array.isArray(propertySchema.enumA) || Array.isArray(propertySchema.enumB) || Array.isArray(propertySchema.enumC))) {
      field.enum = this.request.getNationalities(false);
      return;
    }

    // Use custom enum if provided (fallback when no schema enum found)
    if(options.useCustomEnum) {
      field.enum = options.useCustomEnum();
      return;
    }

    // Nothing to do if no schema property and no custom enum
    if(!propertySchema) return;
  }

  public applySchemaValidations(fieldName: string, formName: VisaFormName, fieldKey: VisaFN) {
    const field = this.request.getField(formName, fieldKey, this.config);
    if(field) {
      let fieldSchema;
      let isRequired = false;

      // Handle nested objects like Documents.PurposeOfVisit
      if(fieldName.includes('.')) {
        const [parentObj, childField] = fieldName.split('.');
        fieldSchema = this.schema.properties[parentObj]?.properties?.[childField];
        isRequired = this.isGloballyRequired(childField);
      } else {
        fieldSchema = this.schema.properties[fieldName];
        isRequired = this.isGloballyRequired(fieldName);
      }

      const validators = [];

      // if onlyCustomValidators is true, add custom validators
      if(field.onlyCustomValidators) {
        validators.push(...field.validators);
      }

      field.dateValidators = field.dateValidators || {};

      if(!field.onlyCustomValidators) {
        if(fieldSchema?.maxDateMilliFrom) {
          field.dateValidators.maxDateMilliFrom = fieldSchema.maxDateMilliFrom;
        }
        if(fieldSchema?.minDateMilliFromNow !== undefined) {
          field.dateValidators.minDateMilliFromNow = fieldSchema.minDateMilliFromNow;
        }
        if(fieldSchema?.maxDateMilliFromNow !== undefined) {
          field.dateValidators.maxDateMilliFromNow = fieldSchema.maxDateMilliFromNow;
        }
        if(fieldSchema?.maxDateMilliTo) {
          field.dateValidators.maxDateMilliTo = fieldSchema.maxDateMilliTo;
        }
        if(fieldSchema?.mustBeEqualOrAfter) {
          field.dateValidators.mustBeEqualOrAfter = fieldSchema.mustBeEqualOrAfter;
        }
        if(fieldSchema?.mustBeEqualOrBefore) {
          field.dateValidators.mustBeEqualOrBefore = fieldSchema.mustBeEqualOrBefore;
        }
        if(fieldSchema?.maxDateMilliFromToday) {
          field.dateValidators.maxDateMilliFromToday = fieldSchema.maxDateMilliFromToday;
        }
        if(isRequired) {
          validators.push(Validators.required.name);
        }
        if(fieldSchema?.maxLength !== undefined) {
          field.maxLength = fieldSchema.maxLength;
          validators.push(Validators.maxLength(fieldSchema.maxLength));
        }
        if(fieldSchema?.minimum) {
          field.minimum = fieldSchema.minimum;
        }
        if(fieldSchema?.maximum) {
          field.maximum = fieldSchema.maximum;
        }
        if(fieldSchema?.frontPattern) {
          field.pattern = fieldSchema.frontPattern;
        } else if(!fieldSchema?.frontPattern && fieldSchema?.pattern) {
          field.pattern = fieldSchema.pattern;
        }
        if(fieldSchema?.enum) {
          field.enum = this.mapSchemaEnumToFieldEnum(fieldSchema.enum);
        }        
      }

      field.validators = validators;

      // Auto-select and hide fields with single enum option that are required (after validators are set)
      if(fieldSchema?.enum) {
        this.handleSingleEnumAutoSelection(field, fieldKey);
      }
    }
  }

  /**
   * Apply an array of JSON Schema conditional rules (allOf entries) either to the root
   * or to the Documents section. Supports if/then/else with properties, anyOf, allOf.
   */
  private processConditionalRules(rules: any[], scope: 'Documents' | 'root'): void {
    if(!Array.isArray(rules)) return;

    rules.forEach((rule: any) => {
      if(!rule || !rule.if) return;

      const conditionMet = this.evaluateSchemaCondition(rule.if);

      // In Documents scope, when a condition is met, reset all document fields first
      if(scope === 'Documents' && conditionMet) {
        this.config[VisaFormName.DOCUMENT]?.fields?.forEach((field: FormField) => {
          if(field.alwaysVisible) return;
          field.validators = [];
          field.hidden = true;
        });
      }

      const applyThen = (thenObj: any) => {
        if(!thenObj) return;

        if(Array.isArray(thenObj.anyOf)) {
          const allFieldsInAnyOf: VisaFN[] = [];
          thenObj.anyOf.forEach((anyOfItem: any) => {
            if(Array.isArray(anyOfItem.required)) {
              anyOfItem.required.forEach((fieldName: string) => {
                if(scope === 'Documents') {
                  const f = this.request.getField(VisaFormName.DOCUMENT, fieldName, this.config);
                  if(f) {
                    f.hidden = false;
                    if(!allFieldsInAnyOf.includes(fieldName as VisaFN)) {
                      allFieldsInAnyOf.push(fieldName as VisaFN);
                    }
                  }
                }
              });
            }
          });

          if(allFieldsInAnyOf.length > 0 && scope === 'Documents') {
            const firstField = this.request.getField(VisaFormName.DOCUMENT, allFieldsInAnyOf[0], this.config);
            if(firstField) {
              firstField.requireOneOf = allFieldsInAnyOf;
            }
          }
        }

        if(Array.isArray(thenObj.required)) {
          thenObj.required.forEach((fieldName: string) => {
            if(fieldName === 'Documents') return; // not a concrete field
            if(scope === 'Documents') {
              // Only touch document fields explicitly in Documents scope
              const f = this.request.getField(VisaFormName.DOCUMENT, fieldName, this.config);
              if(f) {
                f.validators = [Validators.required.name];
                f.hidden = false;
              }
            } else {
              // Root scope: apply across steps
              this.setFieldRequiredAcrossSteps(fieldName, true);
            }
          });
        }
        if(thenObj.properties) {
          Object.keys(thenObj.properties).forEach((propName: string) => {
            this.applyPropertySchemaAcrossSteps(propName, thenObj.properties[propName]);
          });
        }
      };

      const applyElse = (elseObj: any) => {
        if(!elseObj) return;
        if(Array.isArray(elseObj.required)) {
          elseObj.required.forEach((fieldName: string) => {
            if(fieldName === 'Documents') return;
            if(scope === 'Documents') {
              const f = this.request.getField(VisaFormName.DOCUMENT, fieldName, this.config);
              if(f) {
                f.validators = [Validators.required.name];
                f.hidden = false;
              }
            } else {
              this.setFieldRequiredAcrossSteps(fieldName, true);
            }
          });
        }
        if(elseObj.properties) {
          Object.keys(elseObj.properties).forEach((propName: string) => {
            this.applyPropertySchemaAcrossSteps(propName, elseObj.properties[propName]);
          });
        }
      };

      if(conditionMet) {
        applyThen(rule.then);
        // When condition is met, also ensure else.required fields are not enforced if previously applied
        if(scope === 'root' && rule.else?.required) {
          rule.else.required.forEach((fieldName: string) => this.setFieldRequiredAcrossSteps(fieldName, this.isGloballyRequired(fieldName)));
        }
      } else {
        if(rule.else) {
          applyElse(rule.else);
        } else if(scope === 'root' && rule.then?.required) {
          // No else provided: make then.required optional again unless globally required
          rule.then.required.forEach((fieldName: string) => this.setFieldRequiredAcrossSteps(fieldName, this.isGloballyRequired(fieldName)));
        }
      }

      if(scope === 'root' && rule.if?.properties && Array.isArray(rule.then?.required)) {
        rule.then.required.forEach((fieldName: string) => {
          this.steps?.forEach((step: {key: VisaFormName, value: VisaFormName}) => {
            const field = this.request.getField(step.value, fieldName, this.config);
            if(!field) return;
            const vis = field.ifFormControlHasValue ? [field.ifFormControlHasValue.control].flat() as string[] : [];
            const nonVis = Object.entries(rule.if.properties).find(([name]) => !vis.includes(name));
            if(!nonVis) return;
            const [condFieldName, condClause] = nonVis as [string, any];
            const condValue = condClause.const !== undefined ? condClause.const : Array.isArray(condClause.enum) ? condClause.enum : undefined;
            if(condValue !== undefined) field.requiredIfFormControlHasValue = {control: condFieldName as VisaFN, value: condValue};
          });
        });
      }
    });
  }

  /**
   * Evaluate a JSON Schema condition (if) against current form values.
   * Supports properties with const/enum, and nested anyOf/allOf.
   */
  private evaluateSchemaCondition(condition: any, parentValues?: any): boolean {
    if(!condition) return false;

    if(Array.isArray(condition.roleIn)) {
      if(!condition.roleIn.includes(this.api.userInfo.role)) return false;
      if(!condition.anyOf && !condition.allOf && !condition.properties) return true;
    }

    if(Array.isArray(condition.anyOf)) {
      return condition.anyOf.some((sub: any) => this.evaluateSchemaCondition(sub, parentValues));
    }
    if(Array.isArray(condition.allOf)) {
      return condition.allOf.every((sub: any) => this.evaluateSchemaCondition(sub, parentValues));
    }

    if(condition.properties) {
      return Object.keys(condition.properties || condition.properties.Documents.properties).every((fieldName: string) => {
        const clause = condition.properties[fieldName];

        // Gestion des objets imbriqués (ex: Documents)
        let values: any[] = [];
        if(parentValues && typeof parentValues === 'object' && parentValues[fieldName] !== undefined) {
          values = [parentValues[fieldName]];
        } else if(this.schema?.properties?.[fieldName]?.type === 'object' && clause.properties) {
          // On descend dans l'objet imbriqué
          const objValues = this.getFieldValuesAcrossSteps(fieldName)[0]; // On suppose un seul objet
          return this.evaluateSchemaCondition(clause, objValues);
        } else {
          values = this.getFieldValuesAcrossSteps(fieldName);
        }

        // For the RequestReason (with or without _)
        const isCustomer = this.api.userRole.isCustomer;
        const compareValues = (a: string, b: string): boolean => {
          if(typeof a !== 'string' || typeof b !== 'string') return a === b;
          const normA = a.toUpperCase();
          const normB = b.toUpperCase();
          if(isCustomer) {
            return normA === normB || normA === normB + '_' || normA + '_' === normB;
          }
          return normA === normB;
        };

        if(clause.const !== undefined) {
          return values.some((v) => compareValues(v, clause.const));
        }
        if(Array.isArray(clause.enum)) {
          return values.some((v) => clause.enum.some((e: string) => compareValues(v, e)));
        }
        if(Array.isArray(clause.const)) {
          return values.some((v) => clause.enum.some((e: string) => compareValues(v, e)));
        }
        return false;
      });
    }
    return false;
  }

  /** Return all values found for a field across steps (in display order). */
  private getFieldValuesAcrossSteps(fieldName: string): any[] {
    const values: any[] = [];
    this.steps?.forEach((step: { key: VisaFormName, value: VisaFormName }) => {
      const field = this.request.getField(step.value, fieldName, this.config);
      if(field && field.value !== undefined) values.push(field.value);
    });
    return values;
  }

  /**
   * Add or remove only the required validator for a given field across steps,
   * without touching other validators. Also unhides fields when required.
   */
  private setFieldRequiredAcrossSteps(fieldName: string, required: boolean): void {
    this.steps?.forEach((step: { key: VisaFormName, value: VisaFormName }) => {
      const field = this.request.getField(step.value, fieldName, this.config);
      if(field) {
        if(required) {
          const existing = Array.isArray(field.validators) ? field.validators.filter((v) => v !== Validators.required.name) : [];
          field.validators = [...existing, Validators.required.name];

          const originalField = this.cart.getOriginalForm()[step.value]?.fields?.find((f: FormField) => f.name === fieldName);
          if(originalField?.hidden !== true) {
            field.hidden = false;
          }
        } else {
          // Only drop required if not globally required by base schema
          if(!this.isGloballyRequired(fieldName)) {
            if(Array.isArray(field.validators)) {
              field.validators = field.validators.filter((v) => v !== Validators.required.name);
            }
          }
        }
      }
    });
  }

  /** Whether a field is required by the base schema (root or Documents). */
  private isGloballyRequired(fieldName: string): boolean {
    // Check root required
    if(Array.isArray(this.schema?.required) && this.schema.required.includes(fieldName)) return true;
    // Check Documents required
    if(Array.isArray(this.schema?.properties?.Documents?.required) && this.schema.properties.Documents.required.includes(fieldName)) return true;

    // Check root allOf required (dynamic)
    if(Array.isArray(this.schema?.allOf)) {
      for(const rule of this.schema.allOf) {
        if(!rule.if) continue;
        const conditionMet = this.evaluateSchemaCondition(rule.if);
        if(conditionMet && Array.isArray(rule.then?.required) && rule.then.required.includes(fieldName)) return true;
        if(!conditionMet && Array.isArray(rule.else?.required) && rule.else.required.includes(fieldName)) return true;
      }
    }

    // Check Documents allOf required (dynamic)
    if(Array.isArray(this.schema?.properties?.Documents?.allOf)) {
      for(const rule of this.schema.properties.Documents.allOf) {
        if(!rule.if) continue;
        const conditionMet = this.evaluateSchemaCondition(rule.if, this.getFieldValuesAcrossSteps('Documents')[0]);
        if(conditionMet && Array.isArray(rule.then?.required) && rule.then.required.includes(fieldName)) return true;
        if(!conditionMet && Array.isArray(rule.else?.required) && rule.else.required.includes(fieldName)) return true;
      }
    }

    return false;
  }

  /**
   * Apply schema property details (enum, pattern, frontPattern, maxLength) to all
   * matching fields across steps and unhide them.
   */
  private applyPropertySchemaAcrossSteps(fieldName: string, propertySchema: any): void {
    this.steps?.forEach((step: { key: VisaFormName, value: VisaFormName }) => {
      const field = this.request.getField(step.value, fieldName, this.config);
      if(!field) return;

      // enum
      if(Array.isArray(propertySchema.enum)) {
        field.enum = this.mapSchemaEnumToFieldEnum(propertySchema.enum);
      }
      // pattern/frontPattern
      if(propertySchema.frontPattern) {
        field.pattern = propertySchema.frontPattern;
      } else if(!propertySchema.frontPattern && propertySchema.pattern) {
        field.pattern = propertySchema.pattern;
      }
      // maxLength
      if(propertySchema.maxLength !== undefined) {
        field.maxLength = propertySchema.maxLength;
      }

      const originalField = this.cart.getOriginalForm()[step.value]?.fields?.find((f: FormField) => f.name === fieldName);
      if(originalField?.hidden !== true) {
        field.hidden = false;
      }

      // Auto-select and hide fields with single enum option that are required (after all properties are set)
      if(Array.isArray(propertySchema.enum)) {
        this.handleSingleEnumAutoSelection(field, fieldName as VisaFN);
      }
    });
  }

  /**
   * Map schema enum values to field enum format with transformations
   */
  private mapSchemaEnumToFieldEnum(schemaEnum: string[], transformKey: string = 'DYN.'): any[] {
    const mapped = schemaEnum.map((item: string) => ({
      value: item,
      name: this.lang.transform(transformKey + item)
    }));
    // dedupe by value after transform
    const uniqByValue = mapped.filter((opt: any, idx: number, arr: any[]) => arr.findIndex((o: any) => o.value === opt.value) === idx);
    return uniqByValue.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
  }

  /**
   * Handle auto-selection and hiding of select fields with single enum option that are required
   */
  private handleSingleEnumAutoSelection(field: FormField, fieldKey: VisaFN): void {
    // Only process select fields with enum
    if(field.type !== 'select' || !field.enum || !Array.isArray(field.enum)) {
      return;
    }

    // Check if field has exactly one enum option
    if(field.enum.length !== 1) {
      // If enum has multiple options, ensure field is visible (in case it was previously hidden)
      if(field.hidden === true && field.enum.length > 1) {
        field.hidden = false;
      }
      return;
    }

    // Check if field is required (either directly or via validators)
    const isRequired = this.isFieldRequired(field, fieldKey);
    if(!isRequired) {
      return;
    }

    // Auto-select the single option and hide the field
    const singleOption = field.enum[0];
    field.value = singleOption.value;
    field.hidden = true;
  }

  /**
   * Check if a field is required by examining its validators and schema requirements
   */
  private isFieldRequired(field: FormField, fieldKey: VisaFN): boolean {
    // Check if field has required validator
    if(field.validators && field.validators.includes(Validators.required.name)) {
      return true;
    }

    // Check if field is required in the schema
    if(this.schema?.required && this.schema.required.includes(fieldKey)) {
      return true;
    }

    // Check if field is required in Documents schema
    if(this.schema?.properties?.Documents?.required && this.schema.properties.Documents.required.includes(fieldKey)) {
      return true;
    }

    return false;
  }

  protected readonly CurrencySign = CurrencySign;
  protected readonly ShortcutAction = ShortcutAction;
}
